import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogClose, Button, cn } from '@scio3/ui';
import { DOWNGRADE_TRIAL_TO_FREE, DOWNGRADE_PAID_TO_FREE } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { useBillingData } from '../hooks/useBillingData';
import { extractMutationResponse } from '../utils/plan-helpers';

/**
 * Downgrade confirm dialog — used for PAID → Free downgrades only.
 * Matches scio1's confirmVuexyStyleService.confirm({ text: 'freePlanDowngradeMoveTo(AndCancel)', ... }).
 *
 * Trial → Free does NOT route through this dialog. Per scio1 parity
 * (subscriptionPlanModal.js:1008 setStep short-circuit → onFreePlan trial branch),
 * trial users fire the downgrade mutation directly from useSubscriptionWizard.handleSelectFree
 * with no confirm prompt. See that handler for the trial-flow details.
 *
 * No pre-warning feature list is shown — scio1 bypasses validateChangePlan for the Free path.
 */
/**
 * SP53 — backend payment status code for "Free plan device limit exceeded".
 * Mirrors `optisigns-open-api/src/app/shared/common-defs.ts` PAYMENT_STATUS_CODE.LIMIT_QUANTITY (5555).
 * Triggered by `downgradePaidToFreePlan` when the account has more screens than the Free plan allows
 * or has unsupported devices. We open ScreenManagementDialog so the user can fix it inline.
 */
const PAYMENT_STATUS_CODE_LIMIT_QUANTITY = 5555;

export function DowngradeDialog() {
  const { t } = useTranslation();
  const downgradeOpen = useBillingDialogStore((s) => s.downgradeOpen);
  const downgradeContext = useBillingDialogStore((s) => s.downgradeContext);
  const closeDowngrade = useBillingDialogStore((s) => s.closeDowngrade);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);
  const openScreenManagement = useBillingDialogStore((s) => s.openScreenManagement);

  const { isTrial, currency } = useBillingData();
  const subAccountId = downgradeContext.subAccountId as string | undefined;
  // Only hard-reload when the dialog was opened from the paywall lockout
  // (me.accountStatus === false). In normal app sessions, refetchQueries already
  // updates the UI without disrupting the user.
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);

  const [downgradeTrialToFree, { loading: trialLoading }] = useMutation(DOWNGRADE_TRIAL_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });
  const [downgradePaidToFree, { loading: paidLoading }] = useMutation(DOWNGRADE_PAID_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });

  const isSubmitting = trialLoading || paidLoading;

  const handleDowngrade = useCallback(async () => {
    try {
      if (isTrial) {
        const result = await downgradeTrialToFree({
          variables: { subAccountId, currency },
        });
        const response = extractMutationResponse<{
          status?: boolean;
          message?: string;
          code?: number;
        }>(result.data, 'downgradeTrialToFreePlan');
        if (response?.status) {
          toast(t('Your plan has been downgraded to Free'));
          closeDowngrade();
          closeDialog();
          // Paywall path only: hard reload so every Apollo query, Zustand store, and
          // company-scoped cache refreshes with the now-Free plan state and the
          // isAccountInactive gate lifts. Normal sessions rely on refetchQueries.
          if (isAccountInactive) window.location.reload();
          return;
        }
        toast.error(response?.message ?? t('Failed to downgrade'));
      } else {
        const result = await downgradePaidToFree({
          variables: { subAccountId },
        });
        const response = extractMutationResponse<{
          status?: boolean;
          message?: string;
          code?: number;
        }>(result.data, 'downgradePaidToFreePlan');
        if (response?.status) {
          toast.success(
            t('Your subscription will be canceled at the end of the current billing period'),
          );
          closeDowngrade();
          closeDialog();
          if (isAccountInactive) window.location.reload();
          return;
        }
        // SP53 — Free-plan device limit error: hand off to Screen Management dialog
        // so the user can remove or move excess / unsupported screens inline,
        // then manually retry the downgrade. scio1 parity (changeSubscription.js + screenManagementModal).
        if (response?.code === PAYMENT_STATUS_CODE_LIMIT_QUANTITY) {
          closeDowngrade();
          openScreenManagement({
            subAccountId,
            messageKey: 'screenManagementModalFreePlan',
          });
          return;
        }
        // Translate known error keys (e.g. limitFreePlanDeviceDowngrade); fall back to raw message.
        toast.error(
          response?.message
            ? t(response.message, { defaultValue: response.message })
            : t('Failed to downgrade'),
        );
      }
    } catch (err) {
      console.error('Downgrade failed:', err);
      toast.error(t('Failed to downgrade'));
    }
  }, [
    isTrial,
    downgradeTrialToFree,
    downgradePaidToFree,
    closeDowngrade,
    closeDialog,
    openScreenManagement,
    t,
    subAccountId,
    currency,
    isAccountInactive,
  ]);

  const isOpen = downgradeOpen;
  // scio1 uses different copy for trial users (no existing paid subscription to cancel)
  const confirmPromptKey = isTrial ? 'freePlanDowngradeMoveTo' : 'freePlanDowngradeMoveToAndCancel';
  const confirmButtonKey = isTrial ? 'Downgrade' : 'Cancel Subscription & Downgrade';
  const titleKey = isTrial ? 'Move to Free Plan?' : 'Downgrade to Free Plan?';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDowngrade()}>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        data-testid="downgrade-dialog"
        hideCloseButton
      >
        {/* Primary (green) header — scio1 confirmVuexyStyleService bgHeader: "bg-primary"
            (subscriptionPlanModal.js:1006 — for the freePlanDowngradeMoveTo(AndCancel) confirm).
            Plain div instead of DialogHeader to avoid flex-col default clobbering our row layout. */}
        <div className="flex items-center justify-between bg-primary px-5 py-3 text-primary-foreground">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-primary-foreground">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {t(titleKey)}
          </DialogTitle>
          <DialogClose
            className={cn(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
              'text-primary-foreground/80 transition-colors',
              'hover:bg-white/15 hover:text-primary-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
            )}
            aria-label={t('Close')}
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Confirm prompt — matches scio1 freePlanDowngradeMoveTo(AndCancel).
              whitespace-pre-line renders `\n` in the translated string as real line breaks
              (scio1 used <br> tags + ng-bind-html). */}
          <p className="whitespace-pre-line text-sm text-foreground">{t(confirmPromptKey)}</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <DialogClose asChild>
            <Button variant="outline" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button
            data-testid="downgrade-dialog-confirm-btn"
            onClick={handleDowngrade}
            disabled={isSubmitting}
            className={cn('cursor-pointer', isSubmitting && 'cursor-not-allowed opacity-50')}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t(confirmButtonKey)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
